<template>
  <v-sheet class="fs-scheduler">
    <v-toolbar flat class="scheduler-toolbar">
      <v-toolbar-title class="mr-4 font-weight-bold">
        Booking Schedule
      </v-toolbar-title>

      <v-btn outlined class="mr-2" @click="setToday">Today</v-btn>
      <v-btn icon @click="prev"><v-icon>mdi-chevron-left</v-icon></v-btn>
      <v-btn icon @click="next"><v-icon>mdi-chevron-right</v-icon></v-btn>

      <v-toolbar-title class="ml-4">{{ title }}</v-toolbar-title>
      <v-spacer />

      <v-select
        v-model="viewType"
        :items="viewOptions"
        dense
        outlined
        hide-details
        style="max-width: 160px"
        @change="onViewChange"
      />
    </v-toolbar>

    <v-calendar v-if="viewType === 'month'" ref="cal" v-model="value" :type="viewType" :events="calendarEvents"
      :event-color="(e) => e.color" @change="updateTitle" />

    <div v-else-if="viewType === 'day'" class="lanes-wrapper">
      <div ref="timeHeader" class="time-header" @scroll.passive="onHeaderScroll">
        <div class="time-header-inner" :style="{ width: timelineTotalWidthPx }">
          <div v-for="h in hours" :key="`tick-${h}`" class="time-tick" :style="{ left: hourToPx(h) }">
            {{ pad2(h) }}:00
          </div>

          <div v-for="m in gridMinutes" :key="`grid-${m}`" class="grid-line" :style="{ left: minutesToPx(m) }" />
        </div>
      </div>

      <div ref="rows" class="rows" @scroll.passive="onRowsScroll">
        <div v-for="lane in laneItems" :key="lane.id" class="lane">
          <div class="lane-title">
            <v-chip small class="lane-chip" :class="{ 'is-unassigned': lane.isUnassigned }"
              :style="laneChipStyle(laneSummary(lane.id))">
              <v-avatar left size="18" :color="lane.color" class="white--text">
                {{ lane.avatarText }}
              </v-avatar>

              <span class="lane-chip__name">{{ lane.name }}</span>
              <span class="lane-chip__count">{{ laneSummary(lane.id).count }}</span>
              <span class="status-dot" :style="{ background: statusColor(laneSummary(lane.id).status) }" />
            </v-chip>
          </div>

          <div class="row-inner" :class="{ 'is-drop-target': dropTargetKey === lane.id }"
            :style="{ width: timelineTotalWidthPx }" @dragenter.prevent="onDragEnterDropZone(lane.id, $event)"
            @dragover.prevent="onDragOverDropZone"
            @drop.stop.prevent="handleDropDriver(dropLaneDriverId(lane.id), $event)">
            <div v-if="dropTargetKey === lane.id" class="drop-label"
              :style="{ background: hexToRgba(lane.color, 0.9) }">
              <span class="drop-label__avatar" :style="{ background: lane.color }">
                {{ lane.avatarText }}
              </span>
              {{ lane.name }}
            </div>

            <div v-for="ev in dayEventsByLane(lane.id)" :key="ev.id" class="event-block" :class="eventClasses(ev)"
              :style="eventStyleDay(ev)" draggable="true" @dragstart="onDragStart(ev, $event)" @dragend="clearDrag"
              @click.stop="openEventDialog(ev)">
              <div class="event-title">
                <span v-if="isTinyEvent(ev)" class="event-num">#{{ ev.bookingID }}</span>
                <span v-else>{{ ev.bookingID }} - {{ ev.truckNo || "No Truck" }}</span>
              </div>

              <div v-if="!isTinyEvent(ev)" class="event-sub">
                {{ formatHM(ev.startMs) }} - {{ formatHM(ev.endMs) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="week-wrapper">
      <div class="driver-panel">
        <div class="driver-panel__title">Drivers</div>

        <div v-for="lane in laneItems" :key="lane.id" class="driver-item"
          :class="{ 'is-drop-target': dropTargetKey === lane.id, 'is-unassigned': lane.isUnassigned }"
          @dragenter.prevent="onDragEnterDropZone(lane.id, $event)" @dragover.prevent="onDragOverDropZone"
          @drop.stop.prevent="handleDropDriver(dropLaneDriverId(lane.id), $event)">
          <v-chip small class="lane-chip"
            :class="{ 'is-selected': !lane.isUnassigned && selectedDriverIdSet.has(lane.id), 'is-unassigned': lane.isUnassigned }"
            :style="laneChipStyle(laneSummary(lane.id))" @click="!lane.isUnassigned && toggleDriverFilter(lane.id)">
            <v-avatar left size="18" :color="lane.color" class="white--text">
              {{ lane.avatarText }}
            </v-avatar>

            <span class="lane-chip__name">{{ lane.name }}</span>
            <span class="lane-chip__count">{{ laneSummary(lane.id).count }}</span>
            <span class="status-dot" :style="{ background: statusColor(laneSummary(lane.id).status) }" />
          </v-chip>

          <div v-if="lane.isUnassigned && weekEventsByLane(lane.id).length" class="unassigned-list">
            <div v-for="ev in weekEventsByLane(lane.id)" :key="ev.id" class="unassigned-item" draggable="true"
              @dragstart="onDragStart(ev, $event)" @dragend="clearDrag" @click.stop="openEventDialog(ev)">
              <div class="unassigned-item__title">{{ ev.title }}</div>
              <div class="unassigned-item__sub">
                {{ formatHM(ev.startMs) }} - {{ formatHM(ev.endMs) }}
              </div>
            </div>
          </div>
        </div>

        <div class="driver-panel__hint">
          Drag book → drop onto equipment to assign / drop Unassigned to unassign.
        </div>
      </div>

      <div class="week-grid">
        <div class="week-grid__header">
          <div class="week-grid__corner"></div>

          <div class="week-grid__days">
            <div v-for="d in weekDays" :key="d.key" class="day-col-head">
              <div class="dow">{{ d.dow }}</div>
              <div class="dom">{{ d.dom }}</div>
            </div>
          </div>
        </div>

        <div ref="weekScroll" class="week-grid__body" @scroll.passive="onWeekScroll">
          <div class="time-col" :style="{ height: weekBodyHeightPx }">
            <div v-for="h in hours" :key="`wh-${h}`" class="time-label" :style="{ top: weekMinToTop(h * 60) }">
              {{ pad2(h) }}:00
            </div>
          </div>

          <div class="days-area" :style="{ height: weekBodyHeightPx }">
            <div v-for="(d, dayIdx) in weekDays" :key="`col-${d.key}`" class="day-col"
              :class="{ 'is-drop-target': dropTargetKey === `week-day-${dayIdx}` }" :data-day-index="dayIdx"
              @dragenter.prevent="onDragEnterWeekDay(dayIdx, $event)"
              @dragover.prevent="onDragOverWeekDay(dayIdx, $event)"
              @drop.stop.prevent="handleDropWeekDay(d, dayIdx, $event)">
              <div v-for="h in hours" :key="`line-${d.key}-${h}`" class="hour-line"
                :style="{ top: weekMinToTop(h * 60) }" />

              <div v-for="ev in eventsForWeekDayWithLayout(d.startMs, d.endMs)" :key="ev.id" class="week-event"
                :style="eventStyleWeek(ev)" draggable="true" @dragstart="onDragStart(ev, $event)"
                @click.stop="openEventDialog(ev)" @dragend="clearDrag">
                <div class="week-event__title">
                  {{ ev.bookingID }} - {{ ev.truckNo || "No Truck" }}
                </div>

                <div class="week-event__sub">
                  {{ formatHM(ev.startMs) }} - {{ formatHM(ev.endMs) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="toast-stack">
      <transition-group name="toast">
        <div v-for="t in toasts" :key="t.id" class="toast-item" :class="`toast--${t.color}`">
          <span class="toast-item__text">{{ t.text }}</span>
          <button class="toast-item__close" @click="removeToast(t.id)">✕</button>
        </div>
      </transition-group>
    </div>

    <v-dialog v-model="dialog" max-width="500">
      <v-card v-if="selectedEvent">
        <v-card-title>
          Booking {{ selectedEvent.bookingID }}
        </v-card-title>

        <v-card-text>
          <div>
            <b>Truck:</b>
            {{ selectedEvent.truckNo || "No Truck" }}
          </div>
          <div>
            <b>Driver:</b>
            {{ selectedEvent.driverID }} - {{ selectedEvent.driverName }}
          </div>
          <div>
            <b>Phone:</b>
            {{ selectedEvent.driverPhone || "N/A" }}
          </div>
          <div>
            <b>Start:</b>
            <input v-model="dialogStart" type="datetime-local" class="booking-dialog__input">
          </div>

          <div>
            <b>End:</b>
            <input v-model="dialogEnd" type="datetime-local" class="booking-dialog__input">
          </div>

        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeEventDialog">Cancel</v-btn>
          <v-btn color="#006780" dark depressed :loading="dialogSaving" @click="saveEventDialog">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-sheet>
</template>

<script>
import { mapGetters } from "vuex";

const EQUIP_PALETTE = [
  "#031716",
  "#032F30",
  "#0A7075",
  "#0C969C",
  "#6BA3BE",
  "#274D60",
  "#0B525B",
  "#144552",
  "#1B6B6F",
  "#2CA6A4",
  "#3D8B8A",
  "#5CB8B2",
  "#1F4E5F",
  "#2E6F95",
  "#3A86A8",
  "#457B9D",
  "#4D908E",
  "#577590",
  "#2A9D8F",
  "#43AA8B",
  "#5E9C76",
  "#7BA05B",
  "#A7C957",
  "#6C8EAD",
  "#7D6B91",
  "#8D5A97",
  "#A44A7A",
  "#B56576",
  "#BC6C25",
  "#C97B63",
  "#D08C60",
  "#8C6A43",
  "#6D597A",
  "#355070",
  "#588157",
  "#3D405B",
];

const COMPANY = "EPIC03";
const DND_MIME = "text/plain";
const DEFAULT_UNASSIGNED_COLOR = "#757575";
const DRIVER_UNASSIGNED_KEY = "unassigned";

function formatDateLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function normalizeAssignee(v) {
  const s = v == null ? "" : String(v).trim();
  if (!s || s === "0") return null;
  return s;
}

function toSecondsFromMidnight(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return 0;
  }

  return (
    date.getHours() * 3600 +
    date.getMinutes() * 60 +
    date.getSeconds()
  );
}

function buildEventKey({ bookingID, driverID, jobNum }) {
  return [

    String(driverID ?? DRIVER_UNASSIGNED_KEY),
    String(bookingID ?? ""),
    String(jobNum ?? ""),
  ].join("|");
}
function getDurationHours(dateStart, dateEnd) {
  if (!dateStart || !dateEnd) return 0;

  const start = new Date(dateStart);
  const end = new Date(dateEnd);

  if (isNaN(start) || isNaN(end)) return 0;

  const diffMs = end - start;

  return diffMs / (1000 * 60 * 60); // ms → hours
}

function formatEpicorDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}T00:00:00`;
}

function formatDateTimeLocalInput(ms) {
  const d = new Date(ms);
  return `${formatDateLocal(d)}T${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes()
  ).padStart(2, "0")}`;
}

export default {
  name: "DriverScheduling",

  data() {
    return {
      DRIVER_UNASSIGNED_KEY,
      value: formatDateLocal(new Date()),
      viewType: "week",
      viewOptions: [
        { text: "Day", value: "day" },
        { text: "Week", value: "week" },
        { text: "Month", value: "month" },
      ],
      title: "",

      dayStartHour: 0,
      dayEndHour: 24,
      pxPerMin: 2,
      laneGutterPx: 240,
      tinyEventWidthPx: 90,

      weekPxPerMin: 1,

      dragEventId: null,
      dropTargetKey: null,

      syncFromHeader: false,
      syncFromRows: false,

      selectedDriverIds: [],
      toasts: [],
      pendingEventOverrides: {},

      dialog: false,
      selectedEvent: null,
      dialogStart: "",
      dialogEnd: "",
      dialogSaving: false,
    };
  },

  computed: {
    ...mapGetters("Data", ["getBookingPlans", "getDriverList"]),

    dialogCanSave() {
      if (this.dialogSaving) return false;
      if (!this.dialogStart || !this.dialogEnd) return false;

      const start = new Date(this.dialogStart);
      const end = new Date(this.dialogEnd);

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return false;
      }

      return end.getTime() > start.getTime();
    },

    normalizedDriverList() {
      return (this.getDriverList || [])
        .map((item) => {
          const id = normalizeAssignee(
            item.EmpBasic_EmpID || item.UD08_Character05 || item.Character05
          );
          if (!id) return null;

          const name =
            item.EmpBasic_Name ||
            item.Name ||
            item.Driver_Description ||
            id;

          return {
            id,
            name,
            avatarText: this.initials(name),
            color: this.driverColor(id),
          };
        })
        .filter(Boolean);
    },

    driverIdSet() {
      return new Set(this.normalizedDriverList.map((x) => x.id));
    },

    laneItems() {
      return [
        {
          id: DRIVER_UNASSIGNED_KEY,
          name: "Unassigned",
          color: "#6B7280",
          avatarText: this.initials("Unassigned"),
          isUnassigned: true,
        },
        ...this.normalizedDriverList.map((x) => ({
          ...x,
          isUnassigned: false,
        })),
      ];
    },

    books() {
      return (this.getBookingPlans || []).map((row, idx) => this.buildEvent(row, idx));
    },

    callsByLaneMap() {
      const map = new Map();

      for (const book of this.books) {
        const key = this.laneKey(book.driverID);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(book);
      }

      for (const arr of map.values()) {
        arr.sort((a, b) => a.startMs - b.startMs);
      }

      return map;
    },

    totalMinutes() {
      return (this.dayEndHour - this.dayStartHour) * 60;
    },

    timelineWidthPx() {
      return this.totalMinutes * this.pxPerMin;
    },

    timelineTotalWidthPx() {
      return `${this.laneGutterPx + this.timelineWidthPx}px`;
    },

    hours() {
      return Array.from(
        { length: this.dayEndHour - this.dayStartHour },
        (_, i) => this.dayStartHour + i
      );
    },

    gridMinutes() {
      const arr = [];
      for (let m = 0; m <= this.totalMinutes; m += 30) arr.push(m);
      return arr;
    },

    calendarEvents() {
      return this.books.map((c) => ({
        name: c.title,
        start: new Date(c.startMs),
        end: new Date(c.endMs),
        color: c.color || "#0C969C",
      }));
    },

    weekRangeMs() {
      return this.getRangeMs(this.value, "week");
    },

    dayRangeMs() {
      return this.getRangeMs(this.value, "day");
    },

    weekDays() {
      const { startMs } = this.weekRangeMs;

      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(startMs + i * 86400000);
        const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

        return {
          key: `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`,
          startMs: dayStart,
          endMs: dayStart + 86400000,
          dow: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()],
          dom: d.getDate(),
        };
      });
    },

    weekBodyHeightPx() {
      return `${this.totalMinutes * this.weekPxPerMin}px`;
    },

    selectedDriverIdSet() {
      return new Set(this.selectedDriverIds);
    },
  },

  mounted() {
    this.initialize();
  },

  methods: {
    async initialize() {
      await this.loadData();
      this.$nextTick(() => this.updateTitle());
    },

    async loadData() {
      await Promise.all([
        this.$store.dispatch("Data/getBookingPlans"),
        this.$store.dispatch("Data/getDriverList"),
      ]);
      this.updateTitle();
    },

    async reloadCallsFromServer() {
      await this.$store.dispatch("Data/getBookingPlans");
    },

    buildEvent(row, idx) {
      const bookingID = String(row.Key1 || row.UD08_Key1 || "").trim();
      const schedDate = row.Date02 || row.UD08_Date02;
      const endDate = row.Date03 || row.UD08_Date03 || schedDate;
      const schedTime = Number(
        row.Calculated_StartTime ?? row.StartTime ?? row.UD08_Number09 ?? 0
      );
      const safeStartTime = schedTime > 0 ? schedTime : 8 * 3600;
      const endTime = Number(
        row.Calculated_EndTime ?? row.EndTime ?? row.UD08_Number10 ?? 0
      );
      const safeEndTime =
        endTime > 0 ? endTime : safeStartTime + 3600;
      const startMs = this.combineDateAndSecondsToMs(schedDate, safeStartTime);
      const endMs = this.combineDateAndSecondsToMs(endDate, safeEndTime);

      const baseDriverID = normalizeAssignee(
        row.EmpBasic_EmpID || row.UD08_Character05
      );
      const truckNo =
        row.Equip_EquipID ||
        "";

      const driverName =
        row.Name || row.EmpBasic_Name || baseDriverID || "Unassigned";
      const jobNum = row.UD08_Key1;

      const baseKey = buildEventKey({
        driverID: baseDriverID,
        bookingID,
        jobNum,
        driverName
      });

      const override = this.pendingEventOverrides[baseKey] || {};

      const driverID = Object.prototype.hasOwnProperty.call(override, "driverID")
        ? override.driverID
        : baseDriverID;
      const finalDriverName = Object.prototype.hasOwnProperty.call(
        override,
        "driverName"
      )
        ? override.driverName
        : driverID === baseDriverID
          ? driverName
          : this.driverNameById(driverID);
      const durationHours = getDurationHours(schedDate, endDate);
      const finalStartMs = override.startMs ?? startMs;
      const finalEndMs = override.endMs ?? endMs;

      const finalKey = buildEventKey({
        driverID,
        bookingID,
        jobNum,
        driverName
      });

      return {
        rowIndex: idx,
        id: finalKey,
        originalId: baseKey,
        bookingID,
        title: `${bookingID || "No Booking"} - ${truckNo || "No Truck"}`,
        startMs: finalStartMs,
        endMs: finalEndMs,
        truckNo,
        driverID,
        driverName: finalDriverName,
        driverPhone: row.EmpBasic_Phone || "",
        driverDescription: row.Name || row.EmpBasic_Name || finalDriverName,
        color: driverID
          ? this.driverColor(driverID)
          : DEFAULT_UNASSIGNED_COLOR,
        jobNum,
        durationHours,
        calculatedType: row.Calculated_Type || "",
      };
    },

    laneKey(driverID) {
      return driverID ? String(driverID) : DRIVER_UNASSIGNED_KEY;
    },

    dropLaneDriverId(laneId) {
      return laneId === DRIVER_UNASSIGNED_KEY ? null : laneId;
    },

    callsByLane(laneId) {
      return this.callsByLaneMap.get(this.laneKey(laneId)) || [];
    },

    dayEventsByLane(laneId) {
      const { startMs, endMs } = this.dayRangeMs;
      return this.callsByLane(laneId).filter((x) => x.startMs >= startMs && x.startMs < endMs);
    },

    weekEventsByLane(laneId) {
      const { startMs, endMs } = this.weekRangeMs;
      return this.callsByLane(laneId).filter((x) => x.startMs >= startMs && x.startMs < endMs);
    },

    laneSummary(laneId) {
      const list =
        this.viewType === "week"
          ? this.weekEventsByLane(laneId)
          : this.dayEventsByLane(laneId);

      const count = list.length;

      return {
        count,
        status: count > 0 ? "busy" : "available",
      };
    },

    eventsForWeekDay(dayStartMs, dayEndMs) {
      const { startMs, endMs } = this.weekRangeMs;

      return this.books.filter((x) => {
        const inWeek = x.startMs >= startMs && x.startMs < endMs;
        const inDay = x.startMs >= dayStartMs && x.startMs < dayEndMs;

        return inWeek && inDay;
      });
    },

    driverNameById(driverID) {
      if (!driverID) return "Unassigned";
      return this.normalizedDriverList.find((x) => x.id === String(driverID))?.name || "Unassigned";
    },

    toggleDriverFilter(driverID) {
      const id = String(driverID);

      if (this.selectedDriverIdSet.has(id)) {
        this.selectedDriverIds = this.selectedDriverIds.filter((x) => x !== id);
      } else {
        this.selectedDriverIds = [...this.selectedDriverIds, id];
      }
    },

    showToast(text, color = "success") {
      const id = Date.now() + Math.random();
      this.toasts.push({ id, text, color });
      setTimeout(() => this.removeToast(id), 6000);
    },

    removeToast(id) {
      const idx = this.toasts.findIndex((t) => t.id === id);
      if (idx !== -1) this.toasts.splice(idx, 1);
    },

    extractImporterError(parsed) {
      try {
        const rows = parsed?.Data?.Tables?.[0]?.Rows ?? [];
        const failed = rows.find((r) => !r.IsSuccess && r.ImportError?.Message);
        if (!failed) return null;
        const inner = JSON.parse(failed.ImportError.Message);
        return inner?.ErrorMessage ?? failed.ImportError.Message;
      } catch {
        return null;
      }
    },

    makeImporterCallbacks(successMsg, failurePrefix) {
      return {
        onSuccess: () => this.showToast(successMsg, "success"),
        onFailure: (parsed) => {
          const detail = this.extractImporterError(parsed) ?? "Unknown error";
          this.showToast(`${failurePrefix}: ${detail}`, "error");
        },
      };
    },

    baseDayStartMs(dateLike) {
      if (!dateLike) return NaN;
      const d0 = new Date(dateLike);
      if (Number.isNaN(d0.getTime())) return NaN;
      return new Date(d0.getFullYear(), d0.getMonth(), d0.getDate()).getTime();
    },

    combineDateAndSecondsToMs(dateLike, secondsFromMidnight) {
      const base = this.baseDayStartMs(dateLike);
      if (Number.isNaN(base)) return NaN;
      return base + Number(secondsFromMidnight ?? 0) * 1000;
    },

    getRangeMs(valueDateStr, type) {
      const [y, m, d] = valueDateStr.split("-").map(Number);

      // 👇 FIX: tạo local date, không dùng string ISO
      const base = new Date(y, m - 1, d);

      const start = new Date(base);
      const end = new Date(base);

      if (type === "week") {
        const day = (start.getDay() + 6) % 7;
        start.setDate(start.getDate() - day);
        end.setDate(start.getDate() + 7);
      } else {
        end.setDate(start.getDate() + 1);
      }

      return { startMs: start.getTime(), endMs: end.getTime() };
    },

    updateTitle() {
      if (this.viewType === "month" && this.$refs.cal?.title) {
        this.title = this.$refs.cal.title;
        return;
      }

      if (this.viewType === "week") {
        const start = new Date(this.weekRangeMs.startMs);
        const end = new Date(this.weekRangeMs.endMs - 1);
        this.title = `${formatDateLocal(start)} ~ ${formatDateLocal(end)}`;
        return;
      }

      this.title = this.value;
    },

    onViewChange() {
      this.updateTitle();
    },

    prev() {
      if (this.viewType === "month" && this.$refs.cal) {
        this.$refs.cal.prev();
      } else {
        this.shiftRange(-1);
      }
      this.updateTitle();
    },

    next() {
      if (this.viewType === "month" && this.$refs.cal) {
        this.$refs.cal.next();
      } else {
        this.shiftRange(1);
      }
      this.updateTitle();
    },

    setToday() {
      this.value = formatDateLocal(new Date());
      this.updateTitle();
    },

    shiftRange(step) {
      const d = new Date(`${this.value}T00:00:00`);
      d.setDate(d.getDate() + step * (this.viewType === "week" ? 7 : 1));
      this.value = formatDateLocal(d);
    },

    onHeaderScroll() {
      if (this.syncFromRows) return;
      const h = this.$refs.timeHeader;
      const r = this.$refs.rows;
      if (!h || !r) return;
      this.syncFromHeader = true;
      r.scrollLeft = h.scrollLeft;
      this.syncFromHeader = false;
    },

    onRowsScroll() {
      if (this.syncFromHeader) return;
      const h = this.$refs.timeHeader;
      const r = this.$refs.rows;
      if (!h || !r) return;
      this.syncFromRows = true;
      h.scrollLeft = r.scrollLeft;
      this.syncFromRows = false;
    },

    onWeekScroll() { },

    setDragPayload(e, payloadObj) {
      const dt = e?.dataTransfer;
      if (!dt) return;
      const payload = JSON.stringify(payloadObj);
      dt.setData(DND_MIME, payload);
      dt.setData("text", payload);
      dt.effectAllowed = "move";
      dt.dropEffect = "move";
    },

    getDragPayload(e) {
      const dt = e?.dataTransfer;
      if (!dt) return null;

      const raw =
        dt.getData(DND_MIME) ||
        dt.getData("text") ||
        dt.getData("application/json");

      if (!raw) return null;

      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    },

    onDragEnterDropZone(key, e) {
      this.dropTargetKey = key;
      if (e?.dataTransfer) e.dataTransfer.dropEffect = "move";
    },

    onDragOverDropZone(e) {
      if (e?.dataTransfer) e.dataTransfer.dropEffect = "move";
    },

    onDragStart(ev, e) {
      this.dragEventId = ev.id;
      this.setDragPayload(e, {
        id: ev.id,
        originalId: ev.originalId,
        bookingID: ev.bookingID,
        oldDriverId: ev.driverID ?? null,
        jobNum: ev.jobNum,
      });
    },

    clearDrag() {
      this.dragEventId = null;
      this.dropTargetKey = null;
    },

    setPendingEventOverride(eventKey, patch) {
      this.$set(this.pendingEventOverrides, eventKey, {
        ...(this.pendingEventOverrides[eventKey] || {}),
        ...patch,
      });
    },

    clearPendingEventOverride(eventKey) {
      this.$delete(this.pendingEventOverrides, eventKey);
    },

    async getUd08ByKeys(key1, key2, key3 = "") {
      const getResp = await this.$store.dispatch("Data/callBO", {
        bo: "Ice.BO.UD08",
        method: "GetByID",
        body: {
          key1: String(key1),
          key2: String(key2),
          key3: String(key3 || ""),
          key4: "",
          key5: "",
        },
      });

      return JSON.parse(JSON.stringify(getResp?.returnObj || {}));
    },

    async getUd08Rows(whereClauseUD08, whereClauseUD08Attch) {
      const getResp = await this.$store.dispatch("Data/callBO", {
        bo: "Ice.BO.UD08",
        method: "GetRows",
        body: {
          whereClauseUD08,
          whereClauseUD08Attch,
          pageSize: 0,
          absolutePage: 1,
        },
      });

      return JSON.parse(JSON.stringify(getResp?.returnObj || {}));
    },

    async saveUd08(ds) {
      const updateResp = await this.$store.dispatch("Data/callBO", {
        bo: "Ice.BO.UD08",
        method: "Update",
        body: { ds },
      });

      if (updateResp?.error) {
        throw new Error(updateResp.error);
      }

      return updateResp;
    },

    async persistBookingChange({
      book,
      newDriverId,
      newDriverName,
      newStartMs,
      newEndMs,
      updateHistory = false,
    }) {
      const bookingID = String(book.bookingID || "").trim();
      if (!bookingID) throw new Error("Invalid bookingID");

      const hasExplicitDriverChange =
        Object.prototype.hasOwnProperty.call(arguments[0] || {}, "newDriverId");
      const hasExplicitDriverName =
        Object.prototype.hasOwnProperty.call(arguments[0] || {}, "newDriverName");

      const driverID =
        hasExplicitDriverChange
          ? String(newDriverId || "")
          : String(book.driverID || "");
      const driverName =
        hasExplicitDriverName
          ? String(newDriverName || "")
          : driverID
            ? String(book.driverName || this.driverNameById(driverID) || "")
            : "Unassigned";

      const startMs = newStartMs != null ? newStartMs : book.startMs;
      const endMs = newEndMs != null ? newEndMs : book.endMs;
      const hasPositionChange =
        newStartMs != null && Number(newStartMs) !== Number(book.startMs);
      const hasDurationChange =
        newEndMs != null && Number(newEndMs) !== Number(book.endMs);

      const start = new Date(startMs);
      const end = new Date(endMs);

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        throw new Error(`Invalid booking schedule: ${bookingID}`);
      }

      const schedDate = formatEpicorDate(start);
      const schedTime = toSecondsFromMidnight(start);
      const endTime = toSecondsFromMidnight(end);

      const parentDs = await this.getUd08ByKeys(bookingID, "BookingList", "");

      if (!parentDs?.UD08?.length) {
        throw new Error(`UD08 not found: ${bookingID}`);
      }

      const parentRow = parentDs.UD08[0];
      parentRow.Character05 = driverID || "";
      parentRow.Character06 = driverName || "";

      if (hasPositionChange) {
        parentRow.Number09 = schedTime;
        parentRow.Number10 = endTime;
      }

      if (hasDurationChange) {
        parentRow.Date02 = schedDate;
        parentRow.Number10 = endTime;
      }

      parentRow.RowMod = "U";

      await this.saveUd08(parentDs);

      if (hasDurationChange && !hasPositionChange) return true;
      if (!updateHistory) return true;

      if (!driverID) return true;

      const escapedBookingID = String(bookingID).replace(/'/g, "''");
      const escapedDriverID = String(driverID).replace(/'/g, "''");

      const historyDs = await this.getUd08Rows(
        `Company = '${COMPANY}' AND Key1 = '${escapedBookingID}' AND Key2 = 'BookingHistory' AND Key3 = '${escapedDriverID}'`, ''
      );

      if (historyDs?.UD08?.length) {
        const historyRow = historyDs.UD08[0];
        console.log("BookingSchedule updating existing BookingHistory row:", {
          bookingID,
          driverID,
          driverName,
        });
        historyRow.Character05 = driverID;
        historyRow.Character06 = driverName || "";
        historyRow.RowMod = "U";
        await this.saveUd08(historyDs);
        return true;
      }
      const newResp = await this.$store.dispatch("Data/callBO", {
        bo: "Ice.BO.UD08",
        method: "GetaNewUD08",
        body: {
          ds: {
            UD08: [],
          },
        },
      });

      const newDsSource =
        newResp?.returnObj ||
        newResp?.parameters?.ds ||
        newResp?.parameters?.DS ||
        newResp?.raw?.data?.parameters?.ds ||
        newResp?.raw?.data?.parameters?.DS ||
        {};
      const newDs = JSON.parse(JSON.stringify(newDsSource));
      console.log("BookingSchedule GetaNewUD08 dataset:", newDs);

      if (!newDs?.UD08?.length) {
        console.error("BookingSchedule GetaNewUD08 returned empty UD08", {
          bookingID,
          driverID,
          driverName,
          newResp,
          newDsSource,
          newDs,
        });
        throw new Error(
          `Unable to create BookingHistory row for booking ${bookingID} and driver ${driverID}`
        );
      }

      const newRow = newDs.UD08[0];
      newRow.Company = parentRow.Company || COMPANY;
      newRow.Key1 = bookingID;
      newRow.Key2 = "BookingHistory";
      newRow.Key3 = driverID;
      newRow.Key4 = "";
      newRow.Key5 = "";
      newRow.ShortChar03 = "Submitted";
      newRow.Character05 = driverID;
      newRow.Character06 = driverName || "";
      newRow.RowMod = "A";
      console.log(
        "BookingSchedule BookingHistory row before Update:",
        newRow
      );

      await this.saveUd08(newDs);
      return true;
    },

    async handleDropDriver(targetDriverId, e) {
      const drag = this.getDragPayload(e);
      if (!drag) return;

      const oldDriverId = drag.oldDriverId != null ? String(drag.oldDriverId) : null;
      const newDriverId = targetDriverId != null ? String(targetDriverId) : null;
      const newDriverName = newDriverId
        ? this.driverNameById(newDriverId)
        : "Unassigned";

      const oldEvent = this.books.find(
        (x) => x.id === drag.id || x.originalId === drag.originalId
      );

      if (!oldEvent) {
        this.clearDrag();
        return;
      }

      const eventKey = oldEvent.originalId || oldEvent.id;
      const updateHistory = oldDriverId !== newDriverId;

      try {
        this.setPendingEventOverride(eventKey, {
          driverID: newDriverId,
          driverName: newDriverName,
        });
        await this.persistBookingChange({
          book: oldEvent,
          newDriverId,
          newDriverName,
          newStartMs: oldEvent.startMs,
          newEndMs: oldEvent.endMs,
          updateHistory,
        });

        await new Promise((r) => setTimeout(r, 300));
        await this.reloadCallsFromServer();
        this.clearPendingEventOverride(eventKey);
        this.showToast(`Reassigned successfully #${oldEvent.bookingID}`, "success");
      } catch (err) {
        this.clearPendingEventOverride(eventKey);
        console.error("handleDropDriver error", err);
        this.showToast(`Reassign failed #${oldEvent.bookingID}`, "error");
      } finally {
        this.clearDrag();
      }
    },

    onDragEnterWeekDay(dayIdx, e) {
      this.dropTargetKey = `week-day-${dayIdx}`;
      if (e?.dataTransfer) e.dataTransfer.dropEffect = "move";
    },

    onDragOverWeekDay(dayIdx, e) {
      this.dropTargetKey = `week-day-${dayIdx}`;
      if (e?.dataTransfer) e.dataTransfer.dropEffect = "move";
    },

    roundToStep(min, step = 15) {
      return Math.round(min / step) * step;
    },

    getMinutesFromWeekDropEvent(dayEl, e) {
      const rect = dayEl.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const rawMin = y / this.weekPxPerMin;
      return this.clamp(this.roundToStep(rawMin, 15), 0, 24 * 60);
    },

    async handleDropWeekDay(day, dayIdx, e) {
      const drag = this.getDragPayload(e);
      if (!drag) return;

      const oldEvent = this.books.find(
        (x) => x.id === drag.id || x.originalId === drag.originalId
      );

      if (!oldEvent) {
        this.clearDrag();
        return;
      }

      const durationMs = oldEvent.endMs - oldEvent.startMs;
      const newStartMs =
        day.startMs + this.getMinutesFromWeekDropEvent(e.currentTarget, e) * 60 * 1000;
      const newEndMs = newStartMs + durationMs;
      const eventKey = oldEvent.originalId || oldEvent.id;

      try {
        this.setPendingEventOverride(eventKey, {
          startMs: newStartMs,
          endMs: newEndMs,
        });

        await this.persistBookingChange({
          book: oldEvent,
          newDriverId: oldEvent.driverID,
          newDriverName: oldEvent.driverName,
          newStartMs,
          newEndMs,
          updateHistory: false,
        });

        await new Promise((r) => setTimeout(r, 300));

        await this.reloadCallsFromServer();

        this.clearPendingEventOverride(eventKey);
      } catch (err) {
        this.clearPendingEventOverride(eventKey);
        console.error("handleDropWeekDay error", err);
        this.showToast(`Schedule move failed #${oldEvent.bookingID}`, "error");
      } finally {
        this.clearDrag();
      }
    },
    async persistMoveScheduleUd08({ book, newStartMs, newEndMs }) {
      const bookingID = String(book.bookingID || "").trim();
      if (!bookingID) throw new Error("Invalid bookingID");

      const start = new Date(newStartMs);
      const end = new Date(newEndMs);

      const schedDate = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}T00:00:00`;
      const schedTime = toSecondsFromMidnight(start);
      const endTime = toSecondsFromMidnight(end);

      const getResp = await this.$store.dispatch("Data/callBO", {
        bo: "Ice.BO.UD08",
        method: "GetByID",
        body: {
          key1: bookingID,
          key2: "BookingList",
          key3: "",
          key4: "",
          key5: "",
        },
      });

      const ds = JSON.parse(JSON.stringify(getResp?.returnObj || {}));

      if (!ds?.UD08?.length) {
        throw new Error(`UD08 not found: ${bookingID}`);
      }

      const row = ds.UD08[0];
      row.Date02 = schedDate;
      row.Date03 = schedDate;
      row.Number09 = schedTime;        // start seconds
      row.Number10 = endTime;          // end seconds
      row.RowMod = "U";

      const updateResp = await this.$store.dispatch("Data/callBO", {
        bo: "Ice.BO.UD08",
        method: "Update",
        body: { ds },
      });

      if (updateResp?.error) {
        throw new Error(updateResp.error);
      }

      this.showToast(`Move successfully #${bookingID}`, "success");

      return updateResp;
    },
    async UD08({ book }) {
      const bookingID = Number(book.bookingID || book.CallNum);
      if (!bookingID) throw new Error("Invalid Booking");

      const getResp = await this.$store.dispatch("Data/callBO", {
        bo: "Ice.BO.UD08",
        method: "GetByID",
        body: { bookingID },
      });

      const ds = JSON.parse(JSON.stringify(getResp?.returnObj || {}));

      if (!ds?.FSCallhd?.length) {
        throw new Error(`Booking not found: ${bookingID}`);
      }

      const updateResp = await this.$store.dispatch("Data/callBO", {
        bo: "Ice.BO.UD08",
        method: "Update",
        body: { ds },
      });

      if (updateResp?.error) {
        throw new Error(updateResp.error);
      }

      this.showToast(`Schedule updated successfully #${bookingID}`, "success");


      return updateResp;
    },

    eventWidthPx(ev) {
      const leftMin = this.toTimelineMin(ev.startMs);
      const rightMin = this.toTimelineMin(ev.endMs);
      const widthMin = Math.max(15, rightMin - leftMin);
      return widthMin * this.pxPerMin;
    },

    isTinyEvent(ev) {
      return this.eventWidthPx(ev) < this.tinyEventWidthPx;
    },

    eventClasses(ev) {
      return {
        "is-tiny": this.isTinyEvent(ev),
        "is-unassigned": !ev.driverID,
      };
    },

    pad2(n) {
      return String(n).padStart(2, "0");
    },

    clamp(v, min, max) {
      return Math.max(min, Math.min(max, v));
    },

    msToOffsetMin(ms) {
      const d = new Date(ms);
      return d.getHours() * 60 + d.getMinutes() - this.dayStartHour * 60;
    },

    toTimelineMin(ms) {
      return this.clamp(this.msToOffsetMin(ms), 0, this.totalMinutes);
    },

    minutesToPx(min) {
      return `${this.laneGutterPx + min * this.pxPerMin}px`;
    },

    hourToPx(hour) {
      return this.minutesToPx((hour - this.dayStartHour) * 60);
    },

    formatHM(ms) {
      const d = new Date(ms);
      return `${this.pad2(d.getHours())}:${this.pad2(d.getMinutes())}`;
    },

    formatDateTime(ms) {
      const d = new Date(ms);
      return `${formatDateLocal(d)} ${this.pad2(d.getHours())}:${this.pad2(
        d.getMinutes()
      )}`;
    },

    weekMinToTop(min) {
      return `${min * this.weekPxPerMin}px`;
    },

    toDayMinute(ms) {
      const d = new Date(ms);
      return d.getHours() * 60 + d.getMinutes();
    },

    eventStyleWeek(ev) {
      const isUnassigned = !ev.driverID;
      const main = isUnassigned ? "#D97706" : (ev.color || "#0C969C");
      const bg = isUnassigned
        ? "linear-gradient(180deg, rgba(245, 158, 11, 0.24), rgba(255, 247, 237, 0.96))"
        : this.hexToRgba(main, 0.18);

      const startMin = this.toDayMinute(ev.startMs);
      const endMin = this.toDayMinute(ev.endMs);
      const height = Math.max(18, (endMin - startMin) * this.weekPxPerMin);

      const totalCols = Math.max(1, ev._overlapTotalCols || 1);
      const col = ev._overlapCol || 0;
      const gap = 6;
      const usableWidth = `calc((100% - ${gap * (totalCols + 1)}px) / ${totalCols})`;
      const left = `calc(${gap}px + (${col} * (${usableWidth} + ${gap}px)))`;

      return {
        top: `${startMin * this.weekPxPerMin}px`,
        height: `${height}px`,
        width: usableWidth,
        left,
        background: bg,
        borderLeft: `4px solid ${main}`,
        color: isUnassigned ? "#8A4B00" : "#053238",
      };
    },
    eventStyleDay(ev) {
      const isUnassigned = !ev.driverID;
      const main = isUnassigned ? "#D97706" : (ev.color || "#0C969C");
      const bg = isUnassigned
        ? "linear-gradient(180deg, rgba(245, 158, 11, 0.24), rgba(255, 247, 237, 0.96))"
        : this.hexToRgba(main, 0.18);

      const leftMin = this.toTimelineMin(ev.startMs);
      const rightMin = this.toTimelineMin(ev.endMs);
      const widthMin = Math.max(15, rightMin - leftMin);

      return {
        left: `${this.laneGutterPx + leftMin * this.pxPerMin}px`,
        width: `${widthMin * this.pxPerMin}px`,
        top: "50%",
        transform: "translateY(-50%)",
        height: "40px",
        background: bg,
        color: isUnassigned ? "#8A4B00" : "#053238",
        borderLeft: `4px solid ${main}`,
        borderRadius: "10px",
        boxSizing: "border-box",
      };
    },

    statusColor(status) {
      if (status === "busy") return "#F78C6A";
      if (status === "available") return "#06D7A0";
      return "#9E9E9E";
    },

    laneChipStyle(lane) {
      const main = lane.color || "#6B7280";
      return {
        background: this.hexToRgba(main, 0.14),
        color: "#0b1f23",
        border: "none",
      };
    },

    initials(name) {
      const parts = String(name || "").trim().split(/\s+/);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },

    hexToRgba(hex, alpha = 0.15) {
      const h = String(hex || "").replace("#", "");
      if (h.length !== 6) return `rgba(0,0,0,${alpha})`;
      const bigint = parseInt(h, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },

    stringHash(str) {
      return [...String(str)].reduce((a, c) => a + c.charCodeAt(0), 0);
    },

    driverColor(key) {
      return EQUIP_PALETTE[this.stringHash(key) % EQUIP_PALETTE.length];
    },

    openEventDialog(ev) {
      this.selectedEvent = ev;
      this.dialogStart = formatDateTimeLocalInput(ev.startMs);
      this.dialogEnd = formatDateTimeLocalInput(ev.endMs);
      this.dialog = true;
    },
    closeEventDialog() {
      this.dialog = false;
      this.selectedEvent = null;
      this.dialogStart = "";
      this.dialogEnd = "";
      this.dialogSaving = false;
    },
    async saveEventDialog() {
      if (!this.selectedEvent) return;

      const start = new Date(this.dialogStart);
      const end = new Date(this.dialogEnd);

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        this.showToast("Invalid Start or End", "error");
        return;
      }

      if (end.getTime() <= start.getTime()) {
        this.showToast("End must be after Start", "error");
        return;
      }

      const bookingID = String(this.selectedEvent.bookingID || "").trim();
      if (!bookingID) {
        this.showToast("Invalid booking", "error");
        return;
      }

      this.dialogSaving = true;

      try {
        const ds = await this.getUd08ByKeys(bookingID, "BookingList", "");

        if (!ds?.UD08?.length) {
          throw new Error(`UD08 not found: ${bookingID}`);
        }

        const row = ds.UD08[0];
        row.Date01 = formatEpicorDate(start);
        row.Date02 = formatEpicorDate(start);
        row.Date03 = formatEpicorDate(end);
        row.Number09 = toSecondsFromMidnight(start);
        row.Number10 = toSecondsFromMidnight(end);
        row.RowMod = "U";

        await this.saveUd08(ds);
        await this.reloadCallsFromServer();
        this.showToast(`Updated booking #${bookingID}`, "success");
        this.closeEventDialog();
      } catch (err) {
        console.error("saveEventDialog error", err);
        this.showToast(`Update failed #${bookingID}`, "error");
      } finally {
        this.dialogSaving = false;
      }
    },
    eventsForWeekDayWithLayout(dayStartMs, dayEndMs) {
      const events = this.eventsForWeekDay(dayStartMs, dayEndMs)
        .slice()
        .sort((a, b) => {
          if (a.startMs !== b.startMs) return a.startMs - b.startMs;
          return a.endMs - b.endMs;
        });

      const groups = [];
      let currentGroup = [];
      let currentGroupMaxEnd = -Infinity;

      for (const ev of events) {
        if (!currentGroup.length) {
          currentGroup = [ev];
          currentGroupMaxEnd = ev.endMs;
          continue;
        }

        if (ev.startMs < currentGroupMaxEnd) {
          currentGroup.push(ev);
          currentGroupMaxEnd = Math.max(currentGroupMaxEnd, ev.endMs);
        } else {
          groups.push(this.buildOverlapColumns(currentGroup));
          currentGroup = [ev];
          currentGroupMaxEnd = ev.endMs;
        }
      }

      if (currentGroup.length) {
        groups.push(this.buildOverlapColumns(currentGroup));
      }

      return groups.flat();
    },

    buildOverlapColumns(events) {
      const columns = [];
      const laidOut = [];

      for (const ev of events) {
        let placedCol = -1;

        for (let col = 0; col < columns.length; col++) {
          const lastInCol = columns[col][columns[col].length - 1];
          if (lastInCol.endMs <= ev.startMs) {
            columns[col].push(ev);
            placedCol = col;
            break;
          }
        }

        if (placedCol === -1) {
          columns.push([ev]);
          placedCol = columns.length - 1;
        }

        laidOut.push({
          ...ev,
          _overlapCol: placedCol,
        });
      }

      const totalCols = columns.length;

      return laidOut.map((ev) => ({
        ...ev,
        _overlapTotalCols: totalCols,
      }));
    },
  },
};
</script>

<style scoped>
.fs-scheduler {
  width: 100%;
  background: #fff;
}

.scheduler-toolbar {
  border-bottom: 1px solid #eceff1;
}

.lanes-wrapper {
  border-left: 1px solid #eee;
}

.time-header {
  height: 48px;
  overflow-x: auto;
  overflow-y: hidden;
  border-bottom: 1px solid #eee;
  background: #fff;
}

.time-header-inner {
  position: relative;
  height: 48px;
}

.time-tick {
  position: absolute;
  top: 14px;
  transform: translateX(-50%);
  font-size: 12px;
  color: #666;
}

.grid-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: #f0f0f0;
}

.rows {
  height: calc(100vh - 168px);
  overflow: auto;
  background: #fff;
}

.lane {
  position: relative;
  display: block;
}

.lane-title {
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4;
  pointer-events: none;
}

.lane-title .v-chip {
  pointer-events: auto;
}

.row-inner {
  position: relative;
  height: 64px;
  background: #fff;
  transition: box-shadow 0.15s ease, background 0.15s ease;
}

.row-inner::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 1px;
  background: #f0f0f0;
  pointer-events: none;
  z-index: 1;
}

.row-inner.is-drop-target {
  box-shadow: inset 0 0 0 2px #0c969c;
  background: rgba(12, 150, 156, 0.05);
}

.drop-label {
  position: sticky;
  left: 248px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  margin-top: 18px;
  padding: 0 10px 0 4px;
  border-radius: 14px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;
  z-index: 3;
}

.drop-label__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1.5px solid rgba(255, 255, 255, 0.7);
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.lane-chip {
  border-radius: 999px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px 0 6px;
  min-height: 36px;
}

.lane-chip__name {
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lane-chip__count {
  min-width: 22px;
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.06);
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  display: inline-block;
  flex-shrink: 0;
}

.event-block {
  position: absolute;
  z-index: 2;
  border-radius: 10px;
  padding: 6px 10px;
  cursor: grab;
  user-select: none;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  overflow: hidden;
}

.event-title {
  font-size: 12px;
  font-weight: 800;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-sub {
  font-size: 11px;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-block.is-tiny {
  padding: 0 8px;
  align-items: center;
  text-align: center;
}

.event-num {
  font-weight: 900;
}

.week-wrapper {
  display: grid;
  grid-template-columns: 300px 1fr;
  height: calc(100vh - 120px);
  border-top: 1px solid #eee;
}

.driver-panel {
  border-right: 1px solid #eee;
  padding: 12px;
  overflow: auto;
  background: #fff;
}

.driver-panel__title {
  font-weight: 800;
  font-size: 16px;
  margin-bottom: 10px;
}

.driver-panel__hint {
  font-size: 12px;
  opacity: 0.7;
  margin-top: 12px;
}

.driver-item {
  margin-bottom: 16px;
  border-radius: 10px;
  padding: 4px;
  transition: box-shadow 0.15s ease, background 0.15s ease;
}

.driver-item.is-drop-target {
  box-shadow: 0 0 0 2px #0c969c;
  background: rgba(12, 150, 156, 0.05);
}

.lane-chip.is-unassigned {
  background: #fff3d6 !important;
  color: #8a4b00 !important;
}

.lane-chip.is-unassigned .lane-chip__count {
  background: rgba(245, 158, 11, 0.18);
  color: #8a4b00;
}

.lane-chip.is-selected {
  outline: 2px solid rgba(12, 150, 156, 0.35);
}

.unassigned-list {
  margin: 10px 0 8px;
  display: grid;
  gap: 8px;
}

.unassigned-item {
  border: 1px solid rgba(245, 158, 11, 0.28);
  border-radius: 12px;
  padding: 8px 10px;
  cursor: grab;
}

.unassigned-item__title {
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.unassigned-item__sub {
  font-size: 11px;
  opacity: 0.75;
  margin-top: 2px;
}

.week-grid {
  display: grid;
  grid-template-rows: 56px 1fr;
  min-width: 880px;
  background: #fff;
}

.week-grid__header {
  display: grid;
  grid-template-columns: 64px 1fr;
  border-bottom: 1px solid #eee;
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 5;
}

.week-grid__corner {
  border-right: 1px solid #eee;
}

.week-grid__days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.day-col-head {
  padding: 8px 10px;
  border-right: 1px solid #eee;
}

.day-col-head:last-child {
  border-right: none;
}

.dow {
  font-size: 12px;
  opacity: 0.75;
}

.dom {
  font-size: 18px;
  font-weight: 800;
  margin-top: 2px;
}

.week-grid__body {
  display: grid;
  grid-template-columns: 64px 1fr;
  overflow: auto;
  position: relative;
}

.time-col {
  position: relative;
  border-right: 1px solid #eee;
  background: #fff;
}

.time-label {
  position: absolute;
  left: 8px;
  transform: translateY(-50%);
  font-size: 11px;
  color: #777;
}

.days-area {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  position: relative;
  background: #fff;
}

.day-col {
  position: relative;
  border-right: 1px solid #eee;
}

.day-col:last-child {
  border-right: none;
}

.day-col.is-drop-target {
  background: rgba(12, 150, 156, 0.06);
  box-shadow: inset 0 0 0 2px #0c969c;
}

.hour-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: #f0f0f0;
}

.week-event {
  position: absolute;
  left: 6px;
  border-radius: 10px;
  padding: 6px 8px;
  box-sizing: border-box;
  cursor: grab;
  overflow: hidden;
  min-height: 22px;
}

.week-event__title {
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.week-event__sub {
  font-size: 11px;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.toast-stack {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 9999;
  display: flex;
  flex-direction: column-reverse;
  gap: 8px;
  pointer-events: none;
}

.toast-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 280px;
  max-width: 420px;
  padding: 12px 16px;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.25);
  pointer-events: auto;
  margin: 5px;
}

.toast--success {
  background: #4caf50;
}

.toast--error {
  background: #f44336;
}

.toast--info {
  background: #2196f3;
}

.toast--warning {
  background: #ff9800;
}

.toast-item__text {
  flex: 1;
  line-height: 1.4;
}

.toast-item__close {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  cursor: pointer;
  padding: 0 0 0 4px;
  flex-shrink: 0;
}

.toast-item__close:hover {
  color: #fff;
}

.booking-dialog__input {
  display: block;
  width: 100%;
  margin-top: 6px;
  padding: 8px 10px;
  border: 1px solid #d5dbe1;
  border-radius: 8px;
  color: #0b1f23;
  background: #fff;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.25s ease;
}

.toast-enter,
.toast-leave-to {
  opacity: 0;
  transform: translateX(40px);
}
</style>
